import type { ComponentProps, ReactNode } from "react";
import { Link } from "expo-router";
import { Pressable, StyleSheet, Text } from "react-native";

import {
  fonts,
  lightColors,
  palette,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export function AppLink({
  children,
  size = "xs",
  tone = "default",
  underline = true,
  ...props
}: ComponentProps<typeof Link> & {
  children: ReactNode;
  size?: "xs" | "sm" | "base";
  tone?: "default" | "inverse";
  /** Inline links underline; set false for links that sit inside tiles/buttons. */
  underline?: boolean;
}) {
  const { stylesheet, sizeStyles } = useThemedStyles(makeStyles);
  return (
    <Link accessibilityRole="link" asChild {...props}>
      <Pressable>
        {({ hovered, pressed }) => (
          <Text
            style={[
              sizeStyles[size],
              tone === "inverse" ? stylesheet.inverse : stylesheet.default,
              underline ? stylesheet.underline : null,
              tone === "inverse" ? stylesheet.underlineInverse : null,
              hovered || pressed ? stylesheet.hover : null,
            ]}
          >
            {children}
          </Text>
        )}
      </Pressable>
    </Link>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    xs: { fontFamily: fonts.regular, fontSize: 12 },
    sm: { fontFamily: fonts.regular, fontSize: 14 },
    base: { fontFamily: fonts.regular, fontSize: 16 },
    default: { color: colors.heading },
    // Fixed light text for permanently dark-green surfaces (e.g. AuthScreen)
    // -- must not follow the theme, or dark mode flips it to near-black.
    inverse: { color: lightColors.background },
    underline: {
      textDecorationLine: "underline",
      textDecorationColor: palette.brandMint,
    },
    underlineInverse: { textDecorationColor: "rgba(62, 207, 142, 0.6)" },
    hover: { textDecorationLine: "underline" },
  });
  const sizeStyles = {
    xs: stylesheet.xs,
    sm: stylesheet.sm,
    base: stylesheet.base,
  };
  return { stylesheet, sizeStyles };
};
